import { Injectable, inject } from '@angular/core';
import { ALLOW_BRANDING_QUERY_PARAM, BrandingService } from './branding.service';
import { resolveTokenMode } from './token-mode';

const STORAGE_KEY = 'auth_token';
const EXT_VERMITTLER_ID_PARAM = 'extVermittlerId';

/**
 * Session-token precedence (highest wins):
 * 1. Brand fallbackSession (any branded mode) — set in initToken(), setTokenIfAllowed() is a no-op
 * 2. URL ?extVermittlerId= query param (brandFallback allows it at init, brandLocked does not)
 * 3. Restore-response extVermittlerId — set via setTokenIfAllowed(), only in queryParam mode
 * 4. sessionStorage carry-over (mode: queryParam) — bootstrapped in initToken()
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly brandingService = inject(BrandingService);
  private readonly allowQueryParam = inject(ALLOW_BRANDING_QUERY_PARAM);
  private token: string | null = null;

  /** Sets the token only if no brand session is active (any branded mode wins). */
  setTokenIfAllowed(t: string): void {
    const mode = resolveTokenMode(
      this.brandingService.config()?.fallbackSession,
      this.allowQueryParam
    );
    if (mode.kind === 'brandLocked' || mode.kind === 'brandFallback') return;
    this.setToken(t);
  }

  public bootstrapFromStorage() {
    this.token = sessionStorage.getItem(STORAGE_KEY);
  }

  public setToken(t: string) {
    this.token = t;
    sessionStorage.setItem(STORAGE_KEY, t);
  }

  public clear() {
    this.token = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }

  public getToken(): string | null {
    return this.token;
  }
}

export function initToken(): () => void {
  return () => {
    const tokens = inject(TokenService);
    const brandConfig = inject(BrandingService).config();
    const allowQueryParam = inject(ALLOW_BRANDING_QUERY_PARAM);

    const url = new URL(window.location.href);
    const extVermittlerId = url.searchParams.get(EXT_VERMITTLER_ID_PARAM);
    const stripExtVermittlerId = () => {
      if (!url.searchParams.has(EXT_VERMITTLER_ID_PARAM)) return;
      url.searchParams.delete(EXT_VERMITTLER_ID_PARAM);
      window.history.replaceState({}, '', url.toString());
    };

    const mode = resolveTokenMode(brandConfig?.fallbackSession, allowQueryParam);

    switch (mode.kind) {
      case 'queryParam':
        if (extVermittlerId) {
          tokens.setToken(extVermittlerId);
          stripExtVermittlerId();
        } else {
          tokens.bootstrapFromStorage();
        }
        break;

      case 'brandLocked':
        tokens.setToken(mode.fallbackSession);
        stripExtVermittlerId();
        break;

      case 'brandFallback':
        tokens.setToken(extVermittlerId ?? mode.fallbackSession);
        stripExtVermittlerId();
        break;
    }
  };
}
